import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { notificationsAPI } from '../services/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

const POLL_MS = 60 * 1000;

const normalizeNotification = (n) => ({
  id: n.id,
  title: n.title,
  message: n.message,
  type: n.type || 'system',
  priority: n.priority || 'info',
  read: Boolean(n.read),
  source: n.source,
  action: n.action,
  actionData: n.actionData || n.action_data || {},
  timestamp: n.timestamp ? new Date(n.timestamp) : new Date(),
});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    try {
      setLoading(true);
      const data = await notificationsAPI.list({ limit: 100 });
      const list = (data.notifications || []).map(normalizeNotification);
      setNotifications(list);
      setUnreadCount(
        typeof data.unread_count === 'number'
          ? data.unread_count
          : list.filter((n) => !n.read).length
      );
    } catch (err) {
      // Keep existing list on transient failures
      console.warn('Failed to load notifications', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshNotifications();
    if (!isAuthenticated) {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(refreshNotifications, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isAuthenticated, user?.id, refreshNotifications]);

  const addNotification = async (notification) => {
    const payload = {
      title: notification.title || 'Notification',
      message: notification.message || '',
      type: notification.type || 'system',
      priority: notification.priority || 'info',
      action: notification.action || null,
      action_data: notification.actionData || notification.action_data || null,
      source: notification.source || 'user',
    };
    try {
      const created = await notificationsAPI.create(payload);
      const normalized = normalizeNotification(created);
      setNotifications((prev) => [normalized, ...prev]);
      setUnreadCount((c) => c + 1);
      return normalized.id;
    } catch (err) {
      // Optimistic local fallback so disk warnings still show offline
      const local = {
        id: `local-${Date.now()}`,
        timestamp: new Date(),
        read: false,
        ...notification,
        title: payload.title,
        message: payload.message,
        type: payload.type,
        priority: payload.priority,
      };
      setNotifications((prev) => [local, ...prev]);
      setUnreadCount((c) => c + 1);
      return local.id;
    }
  };

  const markAsRead = async (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    if (typeof notificationId === 'number' || /^\d+$/.test(String(notificationId))) {
      try {
        await notificationsAPI.markRead(notificationId);
      } catch (err) {
        console.warn('Failed to mark notification read', err);
      }
    }
  };

  const markAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await notificationsAPI.markAllRead();
    } catch (err) {
      console.warn('Failed to mark all notifications read', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    setNotifications((prev) => {
      const target = prev.find((n) => n.id === notificationId);
      if (target && !target.read) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      return prev.filter((n) => n.id !== notificationId);
    });
    if (typeof notificationId === 'number' || /^\d+$/.test(String(notificationId))) {
      try {
        await notificationsAPI.remove(notificationId);
      } catch (err) {
        console.warn('Failed to delete notification', err);
      }
    }
  };

  const clearAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await notificationsAPI.clearAll();
    } catch (err) {
      console.warn('Failed to clear notifications', err);
    }
  };

  const addPostNotification = (type, message, postId) => {
    const titles = {
      published: 'Post Published Successfully',
      failed: 'Post Failed to Publish',
      scheduled: 'Post Scheduled',
      draft: 'Draft Saved',
    };
    const priorities = {
      published: 'success',
      failed: 'error',
      scheduled: 'info',
      draft: 'info',
    };
    return addNotification({
      type: 'posts',
      title: titles[type] || 'Post Update',
      message,
      priority: priorities[type] || 'info',
      action: type === 'failed' ? 'retry_post' : 'view_post',
      actionData: { postId },
      source: 'user',
    });
  };

  const addAnalyticsNotification = (type, message, data = {}) => {
    const titles = {
      high_engagement: 'High Engagement Alert',
      weekly_report: 'Weekly Report Ready',
      milestone: 'Milestone Reached',
      trend: 'Trending Content',
    };
    return addNotification({
      type: 'analytics',
      title: titles[type] || 'Analytics Update',
      message,
      priority: 'info',
      action: 'view_analytics',
      actionData: data,
      source: 'user',
    });
  };

  const addSystemNotification = (type, message, data = {}) => {
    const titles = {
      reminder: 'Reminder',
      update: 'System Update',
      maintenance: 'Maintenance Notice',
      connection: 'Connection Issue',
    };
    const priorities = {
      reminder: 'warning',
      update: 'info',
      maintenance: 'warning',
      connection: 'error',
    };
    return addNotification({
      type: 'system',
      title: titles[type] || 'System Notification',
      message,
      priority: priorities[type] || 'info',
      action: 'view_system',
      actionData: data,
      source: data?.disk ? 'disk' : 'user',
    });
  };

  const addContentNotification = (type, message, data = {}) => {
    const titles = {
      suggestion: 'Content Suggestion',
      generated: 'Content Generated',
      optimized: 'Content Optimized',
    };
    return addNotification({
      type: 'content',
      title: titles[type] || 'Content Update',
      message,
      priority: 'info',
      action: 'view_content',
      actionData: data,
      source: 'user',
    });
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    refreshNotifications,
    addNotification,
    addPostNotification,
    addAnalyticsNotification,
    addSystemNotification,
    addContentNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
